import { evalFhirPathTyped, formatAddress, toTypedValue } from '@medplum/core'
import type {
  Address,
  Coding,
  Encounter,
  Location,
  Patient,
  Practitioner,
  Resource,
} from '@medplum/fhirtypes'
import { format, parseISO } from 'date-fns'
import { SEEN_MRN_SYSTEM } from '#src/constants.ts'
import { clinicDay } from '#src/dates.ts'
import {
  DERIVED_ENCOUNTER_BINDINGS,
  DERIVED_PATIENT_BINDINGS,
  type BindingValue,
  type DeriveOptions,
  encounterStartDay,
} from '#src/derivedBindings.ts'
import type {
  CodedAnswer,
  ObservationValue,
  ValueKind,
} from '#src/observation-logic/value.ts'

const ENROLLMENT_DATE_URL =
  'https://www.seenhealth.org/fhir/StructureDefinition/enrollment-date'

export type ResourceBindingValueType =
  | 'date'
  | 'dateTime'
  | 'integer'
  | 'reference'
  | 'string'
  | 'text'

export type ResourceContextName =
  | 'encounter'
  | 'location'
  | 'patient'
  | 'practitioner'

export interface ResourceBindingFormatter {
  format: (value: BindingValue) => string
  fallback: string
}

const RESOURCE_BINDING_FORMATTERS = {
  text: {
    format: (value) => (typeof value === 'string' ? value : ''),
    fallback: '',
  },
  'us-date': {
    format: (value) => {
      if (typeof value !== 'string') {
        return ''
      }
      const parsed = parseISO(value)
      return Number.isNaN(parsed.getTime())
        ? value
        : format(parsed, 'MM/dd/yyyy')
    },
    fallback: '',
  },
  'gender-initial': {
    format: (value) => {
      if (value === 'male') {
        return 'M'
      }
      if (value === 'female') {
        return 'F'
      }
      if (value === 'other') {
        return 'O'
      }
      return '?'
    },
    fallback: '?',
  },
  integer: {
    format: (value) => (Number.isInteger(value) ? String(value) : ''),
    fallback: '',
  },
  'coding-display': {
    format: (value) => {
      if (typeof value !== 'object' || value === null) {
        return ''
      }
      const coding = value as Coding
      return coding.display ?? coding.code ?? ''
    },
    fallback: '',
  },
  'postal-address': {
    format: (value) =>
      typeof value === 'object' && value !== null
        ? formatAddress(value as Address)
        : '',
    fallback: '',
  },
} as const satisfies Record<string, ResourceBindingFormatter>

export type ResourceBindingFormatterId =
  keyof typeof RESOURCE_BINDING_FORMATTERS

export interface ResourceBindingDefinition {
  id: string
  label: string
  description: string
  context: ResourceContextName
  resourceType: Resource['resourceType']
  // How the value is read: FHIRPath over the resource, or `derive` in
  // TypeScript when it needs more than the resource (an age needs a day).
  fhirPath?: string
  derive?: (resource: Resource, options: DeriveOptions) => BindingValue[]
  definition: string
  answerTypes: readonly ResourceBindingValueType[]
  // The kind of value a condition compares it as; absent when conditions
  // cannot read it.
  kind?: ValueKind
  // The short name a condition reads it by, such as `age`.
  alias?: string
  // How the condition builder names it, when the picker label is too long.
  conditionLabel?: string
  // Legacy rich-text token, such as {{firstName}}, that this binding replaces.
  token?: string
  formatter?: ResourceBindingFormatterId
  sensitive?: boolean
}

export interface ResourceBindingContext {
  patient?: Patient
  practitioner?: Practitioner
  encounter?: Encounter
  location?: Location
}

const OFFICIAL_NAME =
  "iif(name.where(use='official').exists(), name.where(use='official').first(), iif(name.where(use='usual').exists(), name.where(use='usual').first(), name.first()))"
const HOME_ADDRESS =
  "iif(address.where(use='home').exists(), address.where(use='home').first(), address.first())"

// Engineers own this registry. Authors and AI select an id or a rich-text
// token; they never author executable FHIRPath.
export const RESOURCE_BINDINGS = [
  {
    id: 'participant.official-name',
    label: 'Participant official name',
    description: 'The participant’s current official name.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: `${OFFICIAL_NAME}.select(given.join(' ') + ' ' + family)`,
    definition: 'http://hl7.org/fhir/StructureDefinition/Patient#Patient.name',
    answerTypes: ['string'],
  },
  {
    id: 'participant.given-name',
    label: 'Participant first name',
    description: 'The participant’s current given name.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: `${OFFICIAL_NAME}.given.join(' ')`,
    definition: 'http://hl7.org/fhir/StructureDefinition/Patient#Patient.name',
    answerTypes: ['string'],
    token: 'firstName',
  },
  {
    id: 'participant.family-name',
    label: 'Participant last name',
    description: 'The participant’s current family name.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: `${OFFICIAL_NAME}.family`,
    definition: 'http://hl7.org/fhir/StructureDefinition/Patient#Patient.name',
    answerTypes: ['string'],
    token: 'lastName',
  },
  {
    id: 'participant.birth-date',
    label: 'Participant date of birth',
    description: 'The participant’s current birth date.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: 'birthDate',
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.birthDate',
    answerTypes: ['date'],
    token: 'dob',
    formatter: 'us-date',
  },
  {
    id: 'participant.mrn',
    label: 'Participant MRN',
    description: 'The participant’s Seen medical record number.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: `identifier.where(system='${SEEN_MRN_SYSTEM}').first().value`,
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.identifier',
    answerTypes: ['string'],
    token: 'mrn',
  },
  {
    id: 'participant.enrollment-date',
    label: 'Participant enrollment date',
    description: 'The date the participant enrolled.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: `extension.where(url='${ENROLLMENT_DATE_URL}').first().value`,
    definition: ENROLLMENT_DATE_URL,
    answerTypes: ['date', 'dateTime'],
    token: 'enrollmentDate',
    formatter: 'us-date',
  },
  {
    id: 'participant.gender',
    label: 'Participant gender',
    description: 'The participant’s administrative gender.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: 'gender',
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.gender',
    answerTypes: ['string'],
    token: 'gender',
    formatter: 'gender-initial',
  },
  {
    id: 'participant.email',
    label: 'Participant email',
    description: 'The participant’s primary email address.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: "telecom.where(system='email').first().value",
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.telecom',
    answerTypes: ['string'],
    token: 'email',
  },
  {
    id: 'participant.phone',
    label: 'Participant phone',
    description: 'The participant’s primary phone number.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: "telecom.where(system='phone').first().value",
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.telecom',
    answerTypes: ['string'],
    token: 'phone',
  },
  {
    id: 'participant.address',
    label: 'Participant address',
    description: 'The participant’s current home address.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: HOME_ADDRESS,
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.address',
    answerTypes: ['text'],
    token: 'address',
    formatter: 'postal-address',
  },
  {
    id: 'participant.home-address-line',
    label: 'Participant home address',
    description: 'The street lines of the participant’s current home address.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: `${HOME_ADDRESS}.line.join(', ')`,
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.address.line',
    answerTypes: ['string', 'text'],
  },
  {
    id: 'participant.home-address-city',
    label: 'Participant home city',
    description: 'The city from the participant’s current home address.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: `${HOME_ADDRESS}.city`,
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.address.city',
    answerTypes: ['string'],
  },
  {
    id: 'participant.home-address-state',
    label: 'Participant home state',
    description: 'The state from the participant’s current home address.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: `${HOME_ADDRESS}.state`,
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.address.state',
    answerTypes: ['string'],
  },
  {
    id: 'participant.home-address-postal-code',
    label: 'Participant home ZIP code',
    description: 'The postal code from the participant’s current home address.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: `${HOME_ADDRESS}.postalCode`,
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.address.postalCode',
    answerTypes: ['string'],
  },
  {
    id: 'participant.ssn',
    label: 'Participant SSN',
    description: 'The participant’s Social Security number.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath:
      "identifier.where(system='http://hl7.org/fhir/sid/us-ssn').first().value",
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.identifier.value',
    answerTypes: ['string'],
    sensitive: true,
  },
  {
    id: 'participant.languages',
    label: 'Participant languages',
    description: 'Languages the participant speaks.',
    context: 'patient',
    resourceType: 'Patient',
    fhirPath: 'communication.language.text',
    definition:
      'http://hl7.org/fhir/StructureDefinition/Patient#Patient.communication',
    answerTypes: ['string', 'text'],
  },
  ...DERIVED_PATIENT_BINDINGS,
  {
    id: 'practitioner.official-name',
    label: 'Practitioner official name',
    description: 'The current practitioner’s official name.',
    context: 'practitioner',
    resourceType: 'Practitioner',
    fhirPath: `${OFFICIAL_NAME}.select(given.join(' ') + ' ' + family)`,
    definition:
      'http://hl7.org/fhir/StructureDefinition/Practitioner#Practitioner.name',
    answerTypes: ['string'],
  },
  {
    id: 'practitioner.reference',
    label: 'Practitioner',
    description: 'A reference to the current practitioner.',
    context: 'practitioner',
    resourceType: 'Practitioner',
    fhirPath: '$this',
    definition:
      'http://hl7.org/fhir/StructureDefinition/Practitioner#Practitioner',
    answerTypes: ['reference'],
  },
  {
    id: 'encounter.reference',
    label: 'Encounter',
    description: 'A reference to the current encounter.',
    context: 'encounter',
    resourceType: 'Encounter',
    fhirPath: '$this',
    definition: 'http://hl7.org/fhir/StructureDefinition/Encounter#Encounter',
    answerTypes: ['reference'],
  },
  ...DERIVED_ENCOUNTER_BINDINGS,
] as const satisfies readonly ResourceBindingDefinition[]

export type ResourceBindingId = (typeof RESOURCE_BINDINGS)[number]['id']

export function resourceBinding(
  id: string | null | undefined
): ResourceBindingDefinition | undefined {
  return RESOURCE_BINDINGS.find((binding) => binding.id === id)
}

export function resourceBindingByToken(
  token: string
): ResourceBindingDefinition | undefined {
  return RESOURCE_BINDINGS.find(
    (binding) => 'token' in binding && binding.token === token
  )
}

export interface ResolveBindingOptions {
  // Today in local time; defaults to `today()`.
  today?: string
}

function resolveResourceBinding(
  id: string,
  context: ResourceBindingContext,
  options: ResolveBindingOptions = {}
): BindingValue[] {
  const binding = resourceBinding(id) ?? resourceBindingByToken(id)
  if (!binding) {
    return []
  }
  const resource = context[binding.context]
  if (!resource || resource.resourceType !== binding.resourceType) {
    return []
  }
  // A binding to the record itself answers with a reference to it; FHIRPath
  // has no way to say that.
  if (isReferenceBinding(binding)) {
    return resource.id
      ? [{ reference: `${resource.resourceType}/${resource.id}` }]
      : []
  }
  if (binding.derive) {
    const today = options.today ?? clinicDay()
    return binding.derive(resource, {
      asOf: encounterStartDay(context.encounter) ?? today,
      today,
    })
  }
  if (!binding.fhirPath) {
    return []
  }
  return evalFhirPathTyped(binding.fhirPath, [toTypedValue(resource)]).map(
    (value) => value.value
  )
}

export interface ConditionVariable {
  id: string
  alias: string
  label: string
  kind: ValueKind
  context: ResourceContextName
  // The binding's element reference, such as
  // http://hl7.org/fhir/StructureDefinition/Patient#Patient.gender.
  definition: string
}

export interface ElementReference {
  resourceType: string
  // Relative to the resource, dotted for nested elements; empty for the
  // resource itself.
  path: string
}

// The resource type and element path a definition such as
// http://hl7.org/fhir/StructureDefinition/Patient#Patient.gender names.
export function elementReference(
  definition: string
): ElementReference | undefined {
  const hash = definition.indexOf('#')
  if (hash < 0) {
    return undefined
  }
  const [resourceType, ...path] = definition.slice(hash + 1).split('.')
  return resourceType ? { resourceType, path: path.join('.') } : undefined
}

// The bindings a condition can read, by alias, for the condition builder.
export function conditionVariables(): ConditionVariable[] {
  const bindings: readonly ResourceBindingDefinition[] = RESOURCE_BINDINGS
  return bindings.flatMap((binding) =>
    binding.alias && binding.kind && !binding.sensitive
      ? [
          {
            id: binding.id,
            alias: binding.alias,
            label: binding.conditionLabel ?? binding.label,
            kind: binding.kind,
            context: binding.context,
            definition: binding.definition,
          },
        ]
      : []
  )
}

// A binding's value as a condition compares it, found by id or alias:
// `unavailable` when its context resource is absent, null when the resource
// has no value.
export function resourceBindingConditionValue(
  idOrAlias: string,
  context: ResourceBindingContext,
  options: ResolveBindingOptions = {}
): ObservationValue | null | 'unavailable' {
  const bindings: readonly ResourceBindingDefinition[] = RESOURCE_BINDINGS
  const binding = bindings.find(
    (candidate) => candidate.id === idOrAlias || candidate.alias === idOrAlias
  )
  if (!binding?.kind) {
    return 'unavailable'
  }
  const resource = context[binding.context]
  if (!resource || resource.resourceType !== binding.resourceType) {
    return 'unavailable'
  }
  const [value] = resolveResourceBinding(binding.id, context, options)
  return toConditionValue(binding.kind, value)
}

function toConditionValue(
  kind: ValueKind,
  value: BindingValue
): ObservationValue | null {
  switch (kind) {
    case 'number':
      return typeof value === 'number' ? { kind, value } : null
    case 'date':
      return typeof value === 'string'
        ? { kind, value: value.slice(0, 10) }
        : null
    case 'string':
      return typeof value === 'string' ? { kind, value } : null
    case 'boolean':
      return typeof value === 'boolean' ? { kind, value } : null
    case 'coding': {
      if (typeof value === 'string') {
        return { kind, value: [{ code: value }] }
      }
      if (typeof value === 'object' && value !== null && 'code' in value) {
        const { code } = value
        const system =
          'system' in value && typeof value.system === 'string'
            ? value.system
            : undefined
        const display =
          'display' in value && typeof value.display === 'string'
            ? value.display
            : undefined
        if (typeof code !== 'string') {
          return null
        }
        const answer: CodedAnswer = { code }
        if (system) {
          answer.system = system
        }
        if (display) {
          answer.display = display
        }
        return { kind, value: [answer] }
      }
      return null
    }
  }
}

function isReferenceBinding(binding: ResourceBindingDefinition): boolean {
  return (
    binding.answerTypes.length === 1 && binding.answerTypes[0] === 'reference'
  )
}

export function resourceBindingText(
  idOrToken: string,
  context: ResourceBindingContext
): string | undefined {
  const binding =
    resourceBinding(idOrToken) ?? resourceBindingByToken(idOrToken)
  if (!binding) {
    return undefined
  }
  const formatter = resourceBindingFormatter(binding)
  const values = resolveResourceBinding(binding.id, context)
    .map((value) => formatter.format(value))
    .filter((value) => value.length > 0)
  return values.length > 0 ? values.join(', ') : formatter.fallback
}

function resourceBindingFormatter(
  binding: ResourceBindingDefinition
): ResourceBindingFormatter {
  return RESOURCE_BINDING_FORMATTERS[binding.formatter ?? 'text']
}
